import { Injectable } from '@angular/core';
import { BehaviorSubject, map, Observable, shareReplay, tap } from 'rxjs';
import { ApChargesTableDataModel } from './ap-charges.model';
import { CommonService } from '../../services/common.service';
import {
  CrudActions,
  MasterLookup,
  SingleDeleteHeaderText,
  StatusCodes,
} from '../../utility/constants';
import { URLConstants } from '../../utility/url-constants';
import { BaseService } from 'src/app/core/services/base.httpService';
import { handleErrorMessage } from '../../operators/error.operator';
import {
  apChargeDraftRowHasData,
  apChargeStoreHasIncompletePendingRows,
} from '../charge-grid-save-validation.util';

export interface ApChargesFormObject extends ApChargesTableDataModel {
  isInputRow?: boolean;
  isRowDisable?: boolean;
  action?: string;
}

@Injectable({ providedIn: 'root' })
export class ApChargesService {
  private readonly apChargesBehaviorSubject = new BehaviorSubject<
    ApChargesFormObject[]
  >([]);
  private readonly dropdownsLoadedSubject = new BehaviorSubject<boolean>(false);
  private deletedApCharges: ApChargesFormObject[] = [];
  private apInvoiceNumberOptions$?: Observable<any>;
  private agentFieldData: any[] = [];
  private currencyFieldData: any[] = [];
  private agentChargeCodeFieldData: any[] = [];
  private id: number | null = null;
  private ext: string | null = null;
  private destinationId: number | null = null;
  private weight: number | null = null;
  private hasUnsavedChanges = false;
  private chargeGridValidationBlocked = false;
  private isFromHawb: boolean = false;
  private readonly errorHandler = handleErrorMessage();

  constructor(
    private readonly commonService: CommonService,
    private readonly baseService: BaseService
  ) {}

  public setDetailsForAPTab(
    id: number | null,
    ext: string | null,
    destinationId: number | null,
    weight: number | null,
    isFromHawb: boolean
  ) {
    this.id = id;
    this.ext = ext;
    this.destinationId = destinationId;
    this.weight = weight;
    this.isFromHawb = isFromHawb;
  }

  public getDetailsForAPTab(): {
    id: number | null;
    ext: string | null;
    destinationId: number | null;
    weight: number | null;
    isFromHawb: boolean;
  } {
    return {
      id: this.id,
      ext: this.ext,
      destinationId: this.destinationId,
      weight: this.weight,
      isFromHawb: this.isFromHawb,
    };
  }

  public setAgentFieldData(data: any[]) {
    this.agentFieldData = data || [];
    this.checkAllDropdownsHaveData();
  }

  public setCurrencyFieldData(data: any[]) {
    this.currencyFieldData = data || [];
    this.checkAllDropdownsHaveData();
  }

  public setAgentChargeCodeFieldData(data: any[]) {
    this.agentChargeCodeFieldData = data || [];
    this.checkAllDropdownsHaveData();
  }

  private checkAllDropdownsHaveData(): void {
    const allHaveData =
      this.agentFieldData.length > 0 &&
      this.currencyFieldData.length > 0 &&
      this.agentChargeCodeFieldData.length > 0;

    if (allHaveData && !this.dropdownsLoadedSubject.value) {
      this.dropdownsLoadedSubject.next(true);
    }
  }

  public get dropdownsLoaded$(): Observable<boolean> {
    return this.dropdownsLoadedSubject.asObservable();
  }

  public getAgentFieldData(): any[] {
    return this.agentFieldData;
  }

  public getCurrencyFieldData(): any[] {
    return this.currencyFieldData;
  }

  public getAgentChargeCodeFieldData(): any[] {
    return this.agentChargeCodeFieldData;
  }

  public setAPTabDetailsCollection(apData: any[]) {
    const processedData = this.processGroupedAPInvoiceData(apData);
    this.deletedApCharges = [];
    this.hasUnsavedChanges = false;
    this.apChargesBehaviorSubject.next(processedData);
  }

  public updateAPChargesCollection(charges: ApChargesFormObject[]) {
    this.hasUnsavedChanges = true; // Mark as having unsaved changes
    this.apChargesBehaviorSubject.next(charges);
  }

  public updateDeletedAPCharges(deletedCharges: ApChargesFormObject[]) {
    this.deletedApCharges = deletedCharges;
    if (deletedCharges.length > 0) {
      this.hasUnsavedChanges = true; // Mark as having unsaved changes if there are deleted charges
    }
  }

  public getDeletedApCharges(): ApChargesFormObject[] {
    return [...this.deletedApCharges];
  }

  public getHasUnsavedChanges(): boolean {
    return this.hasUnsavedChanges;
  }

  public resetUnsavedChanges(): void {
    this.hasUnsavedChanges = false;
  }

  public setChargeGridValidationBlocked(blocked: boolean): void {
    this.chargeGridValidationBlocked = blocked;
  }

  public getChargeGridValidationBlocked(): boolean {
    return this.chargeGridValidationBlocked;
  }

  public hasIncompletePendingChargeRowsInStore(): boolean {
    return apChargeStoreHasIncompletePendingRows(
      this.apChargesBehaviorSubject.getValue()
    );
  }

  public addAPCharge(charge: ApChargesFormObject) {
    const currentCharges = this.apChargesBehaviorSubject.getValue();
    this.apChargesBehaviorSubject.next([...currentCharges, charge]);
  }

  private processGroupedAPInvoiceData(
    groupedInvoiceData: any[]
  ): ApChargesFormObject[] {
    const apChargesCollection: ApChargesFormObject[] = [];
    groupedInvoiceData.forEach(invoice => {
      if (invoice.apCharges && Array.isArray(invoice.apCharges)) {
        invoice.apCharges.forEach((charge: any) => {
          const apChargeObj: ApChargesFormObject = {
            apInvoiceId: Number(invoice.id) || Number(charge.apInvoiceId) || 0,
            invoiceNumber: invoice.invoiceNumber ?? '',
            invoiceDate: invoice.invoiceDate ?? '',
            agentId: invoice.agentId ?? null,
            agentChargeCode: charge.agentChargeCode ?? '',
            agentChargeCodeId: charge.agentChargeCodeId ?? null,
            agentChargeCodeDescription: charge.agentChargeDescription ?? '',
            chargeAmount: charge.chargeAmount ?? 0,
            incomingChargeAmount: charge.incomingChargeAmount ?? 0,
            incomingChargeCurrency: charge.incomingChargeCurrency ?? '',
            incomingChargeCurrencyId: charge.incomingChargeCurrencyId ?? null,
            conversionRate: charge.conversionRate ?? 0,
            weight: charge.weight ?? 0,
            shipmentIdentifierValue: charge.shipmentIdentifierValue ?? '',
            shipmentIdentifier: charge.shipmentIdentifier ?? '',
            shipmentIdentifierId: charge.shipmentIdentifierId ?? null,
            invoiceChargesId: charge.invoiceChargesId ?? null,
            shipmentId: charge.shipmentId ?? null,
            destinationId: charge.destinationId ?? null,
            tempId: this.commonService.generateUniqueTempId(),
            isLocked: charge.isLocked === true,
            arInvoiceNumber: charge.arInvoiceNumber ?? '',
            isInputRow: false,
            action: 'u',
          };
          apChargesCollection.push(apChargeObj);
        });
      }
    });
    return apChargesCollection;
  }

  public get getAPTabDetailsCollection$(): Observable<ApChargesFormObject[]> {
    return this.apChargesBehaviorSubject.asObservable();
  }

  public getTransformedData(): ApChargesFormObject[] {
    const items = this.apChargesBehaviorSubject.getValue();
    return items.map((item: ApChargesFormObject) => {
      const transformedItem = { ...item };
      if (transformedItem.agentId) {
        const agent = this.agentFieldData.find(
          (a: any) => Number(a.id) === Number(transformedItem.agentId)
        );
        if (agent) {
          transformedItem.agentId = agent.name;
          transformedItem.agent = agent.id;
        }
      }
      if (transformedItem.incomingChargeCurrencyId) {
        const currency = this.currencyFieldData.find(
          (c: any) =>
            Number(c.id) === Number(transformedItem.incomingChargeCurrencyId)
        );
        if (currency) {
          transformedItem.incomingChargeCurrency = currency.name;
        }
      }
      if (transformedItem.agentChargeCodeId) {
        const chargeCode = this.agentChargeCodeFieldData.find(
          (cc: any) =>
            Number(cc.id) === Number(transformedItem.agentChargeCodeId)
        );
        if (chargeCode) {
          transformedItem.agentChargeCode = chargeCode.name;
        }
      }
      return transformedItem;
    });
  }

  public transformIdsToDisplayNames(): void {
    const transformed = this.getTransformedData();
    this.apChargesBehaviorSubject.next(transformed);
  }

  get apChargesCollection(): any[] {
    // Committed rows plus draft rows with values (saved with Shipment/MAWB); blank drafts are dropped.
    const rows = this.apChargesBehaviorSubject
      .getValue()
      .filter((row: ApChargesFormObject) => {
        return (
          row.isRowDisable ||
          (row.isRowDisable === false && apChargeDraftRowHasData({ ...row }))
        );
      });
    const allRows = [...rows, ...this.deletedApCharges];
    const parentDetails = this.getDetailsForAPTab();
    const groupedByInvoice = allRows.reduce(
      (acc: any, row: ApChargesFormObject) => {
        const invoiceId = row.apInvoiceId || 0;
        if (!acc[invoiceId]) {
          acc[invoiceId] = [];
        }
        acc[invoiceId].push(row);
        return acc;
      },
      {}
    );
    const result: any[] = [];
    for (const invoiceId in groupedByInvoice) {
      const charges = groupedByInvoice[invoiceId];
      if (charges.length === 0) continue;
      const firstCharge = charges[0];
      const payload = {
        apInvoiceId: firstCharge.apInvoiceId || 0,
        agentId: firstCharge.agent || 0,
        invoiceNumber: firstCharge.invoiceNumber || '',
        invoiceDate: firstCharge.invoiceDate || '',
        incomingChargeCurrencyId: firstCharge.incomingChargeCurrencyId || 0,
        conversionRate: (firstCharge.conversionRate || 1).toString(),
        apCharges: charges.map((row: ApChargesFormObject) => {
          return {
            apInvoiceId: row.apInvoiceId || 0,
            invoiceChargesId: row.invoiceChargesId || 0,
            action: row.action || 'c',
            agentChargeCodeId: row.agentChargeCodeId || 0,
            agentChargeDescription: row.agentChargeCodeDescription || '',
            incomingChargeCurrencyId: row.incomingChargeCurrencyId || 0,
            conversionRate: row.conversionRate.toString() || '1',
            incomingChargeAmount: Number.parseFloat(
              row.incomingChargeAmount?.toString() || '0'
            ),
            chargeAmount: Number.parseFloat(
              row.chargeAmount?.toString() || '0'
            ),
            shipmentIdentifier: parentDetails.isFromHawb
              ? SingleDeleteHeaderText.Shipment
              : SingleDeleteHeaderText.MawbCaps,
            shipmentIdentifierId: row.shipmentIdentifierId || 0,
            shipmentIdentifierValue: this.commonService.isValidField(
              row.shipmentIdentifierValue
            )
              ? row.shipmentIdentifierValue
              : parentDetails.ext ?? '',
            shipmentId: parentDetails.id ?? row.shipmentId ?? 0,
            weight: this.commonService.isValidField(row.weight)
              ? +row.weight
              : parentDetails.weight ?? 0,
            destinationId: this.commonService.isValidField(
              row.destinationId ?? 0
            )
              ? +(row.destinationId ?? 0)
              : parentDetails.destinationId ?? 0,
          };
        }),
      };
      result.push(payload);
    }
    return result;
  }

  public getCurrencyOptions(): Observable<any> {
    return this.commonService.getMasterLookUp(MasterLookup.currency).pipe(
      map((data: any) => {
        if (data.statusCode === StatusCodes.Success) {
          return { statusCode: data.statusCode, data: data.data };
        }
        return { statusCode: data.statusCode, data: [] };
      })
    );
  }

  public getAgentOptions(): Observable<any> {
    return this.commonService.getMasterLookUp(MasterLookup.agentInfo).pipe(
      map((data: any) => {
        if (data.statusCode === StatusCodes.Success) {
          return { statusCode: data.statusCode, data: data.data };
        }
        return { statusCode: data.statusCode, data: [] };
      })
    );
  }

  public getAgentCodeOptions(): Observable<any> {
    return this.commonService
      .getMasterLookUp(MasterLookup.agentChargeCode)
      .pipe(
        map((data: any) => {
          if (data.statusCode === StatusCodes.Success) {
            return { statusCode: data.statusCode, data: data.data };
          }
          return { statusCode: data.statusCode, data: [] };
        })
      );
  }

  public validateInvoiceNumber(invoiceNumber: string): Observable<any> {
    const url = `${URLConstants.apInvoiceValidate}/${invoiceNumber}`;
    return this.baseService.initiateApiRequest(
      'GET',
      url,
      invoiceNumber,
      'json'
    );
  }

  /** Fetched once and replayed; filtering happens client-side. A failed call is not cached. */
  public getApInvoiceNumberOptions(): Observable<any> {
    this.apInvoiceNumberOptions$ ??= this.baseService
      .initiateApiRequest('GET', URLConstants.apInvoiceNumbers, '', 'json')
      .pipe(
        this.errorHandler(),
        tap({ error: () => (this.apInvoiceNumberOptions$ = undefined) }),
        shareReplay(1)
      );
    return this.apInvoiceNumberOptions$;
  }

  public createApCharge(payload: any): Observable<any> {
    return this.baseService
      .initiateApiRequest('PUT', URLConstants.createAPcharges, payload, 'json')
      .pipe(this.errorHandler());
  }

  public updateApCharge(payload: any): Observable<any> {
    return this.baseService
      .initiateApiRequest('POST', URLConstants.updateAPcharges, payload, 'json')
      .pipe(this.errorHandler());
  }

  public deleteApCharge(payload: any): Observable<any> {
    return this.baseService.initiateApiRequest(
      'POST',
      URLConstants.deleteAPcharges,
      payload,
      'json'
    );
  }

  public clear(): void {
    this.apChargesBehaviorSubject.next([]);
    this.deletedApCharges = [];
    this.dropdownsLoadedSubject.next(false);
    this.agentFieldData = [];
    this.currencyFieldData = [];
    this.agentChargeCodeFieldData = [];
    this.id = null;
    this.ext = null;
    this.destinationId = null;
    this.hasUnsavedChanges = false;
    this.chargeGridValidationBlocked = false;
  }

  /** One invoice payload; every row in `apChargesData` must belong to the same invoice. */
  public buildPayloadForSaveOrUpdateAPChargeInViewMode(
    apChargesData: any,
    isAddClicked: boolean
  ): any {
    const details = this.getDetailsForAPTab();
    const isHawbContext = details.isFromHawb;
    const firstCharge = apChargesData[0];
    const payload = {
      apInvoiceId: firstCharge.apInvoiceId,
      agentId: firstCharge.agent,
      invoiceNumber: firstCharge.invoiceNumber,
      invoiceDate: firstCharge.invoiceDate,
      incomingChargeCurrencyId: firstCharge.incomingChargeCurrencyId,
      conversionRate: firstCharge.conversionRate.toString(),
      apCharges: apChargesData.map((charge: any) => ({
        apInvoiceId: charge.apInvoiceId,
        invoiceChargesId: this.commonService.isValidField(
          charge.invoiceChargesId
        )
          ? +charge.invoiceChargesId
          : 0,
        action: isAddClicked ? CrudActions.create : CrudActions.update,
        agentChargeCodeId: charge.agentChargeCodeId,
        agentChargeDescription: charge.agentChargeCodeDescription,
        incomingChargeCurrencyId: charge.incomingChargeCurrencyId,
        conversionRate: charge.conversionRate,
        incomingChargeAmount: Number.parseFloat(charge.incomingChargeAmount),
        chargeAmount: Number.parseFloat(charge.chargeAmount),
        shipmentIdentifier: isHawbContext
          ? SingleDeleteHeaderText.Shipment
          : SingleDeleteHeaderText.MawbCaps,
        shipmentIdentifierId: this.commonService.isValidField(
          charge.shipmentIdentifierId
        )
          ? +charge.shipmentIdentifierId
          : 0,
        shipmentIdentifierValue: details.ext ?? '',
        shipmentId: details.id ?? 0,
        weight: details.weight ?? 0,
        destinationId: details.destinationId ?? 0,
      })),
    };
    return payload;
  }

  public buildPayloadForDeleteAPChargeInViewMode(apChargesData: any): any {
    const groupedByInvoice = apChargesData.reduce((acc: any, apCharge: any) => {
      const invoiceId = apCharge.apInvoiceId;
      if (!acc[invoiceId]) {
        acc[invoiceId] = {
          apInvoiceId: invoiceId,
          agentId: apCharge.agent,
          apCharges: [],
        };
      }
      acc[invoiceId].apCharges.push({
        shipmentIdentifierId: apCharge.shipmentIdentifierId,
        invoiceChargesId: apCharge.invoiceChargesId,
        action: CrudActions.delete,
      });
      return acc;
    }, {});
    return {
      ap: Object.values(groupedByInvoice),
    };
  }
}
